import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import React, { useState } from 'react';

interface ToolInvocationProps {
  toolName: string;
  state: string;
  args?: Record<string, unknown>;
  result?: unknown;
}

function getToolSummary(toolName: string, result: unknown): string {
  if (!result || typeof result !== 'object') return 'Done';
  const res = result as Record<string, unknown>;

  if (toolName === 'listCaseRecords' && Array.isArray(res.case_records)) {
    return `${res.case_records.length} case(s) found`;
  }
  if (toolName === 'getCaseRecord' && res.case_record) {
    return 'Case loaded';
  }
  if (toolName === 'listEvaluations' && Array.isArray(res.evaluations)) {
    return `${res.evaluations.length} evaluation(s)`;
  }
  if (toolName === 'getEvaluationCounts' && res.total !== undefined) {
    return `Total: ${res.total}`;
  }
  if (toolName === 'getCallSummaries' && Array.isArray(res.summaries)) {
    return `${res.summaries.length} call summary(ies)`;
  }
  if (toolName === 'suggestFollowUps' && Array.isArray(res.suggestions)) {
    return `${res.suggestions.length} suggestion(s)`;
  }
  return 'Done';
}

export function ChatToolInvocation({ toolName, state, result }: ToolInvocationProps) {
  const [expanded, setExpanded] = useState(false);

  // Hide suggestFollowUps tool display entirely — it's consumed by ChatInput
  if (toolName === 'suggestFollowUps') return null;

  const isLoading = state === 'call' || state === 'partial-call';

  return (
    <div className="my-1 rounded border bg-muted/50 text-xs">
      <button
        type="button"
        className="flex w-full items-center gap-1.5 px-2 py-1.5 text-left text-muted-foreground hover:text-foreground"
        onClick={() => !isLoading && setExpanded(!expanded)}
        disabled={isLoading}
      >
        {isLoading ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : expanded ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronRight className="h-3 w-3" />
        )}
        <span className="font-medium">{toolName}</span>
        {!isLoading && result !== undefined && (
          <span className="ml-auto text-muted-foreground">{getToolSummary(toolName, result)}</span>
        )}
        {isLoading && <span className="ml-auto text-muted-foreground">Running...</span>}
      </button>
      {expanded && result !== undefined && (
        <pre className="max-h-48 overflow-auto border-t px-2 py-1.5 text-muted-foreground">
          {JSON.stringify(result, null, 2)}
        </pre>
      )}
    </div>
  );
}
